import CallError from '../../../utils/CallError';
import { getBadParams, getMissingKeys } from '../../../utils/general';
import { reviewSettings } from '../setup';

/**
 * @typedef AddReplyResponse
 * @type {Object}
 * @property {bool} authorIsDeveloper
 * @property {string} authorName
 * @property {string} createdAt
 * @property {string} editedAt
 * @property {string} id
 * @property {bool} isEditable
 * @property {string} reviewId
 * @property {string} title
 * @property {string} text
 */

/**
 * Updates a reply
 * @param {object} params - an object of params
 * @param {string} params.accessToken - IMS access token
 * @param {string} params.replyId - ID of the reply to update
 * @param {string} params.imsUserId - User's IMS userId
 * @param {string} params.title - Title of the reply
 * @param {string} params.text - Text of the reply
 * @param {AbortSignal} [signal] - Abort signal that can be used to abort the fetch request
 * @returns {Promise<AddReplyResponse>}
 * @throws {CallError}
 */
export default async (params = {}, signal = null) => {
  const requiredParams = ['accessToken', 'replyId', 'imsUserId', 'text', 'authorName'];
  const paramsRules = {
    accessToken: { allowedTypes: ['string'] },
    replyId: { allowedTypes: ['string'] },
    imsUserId: { allowedTypes: ['string'] },
    title: { allowedTypes: ['string'] },
    text: { allowedTypes: ['string'] },
    authorName: { allowedTypes: ['string'] },
  };

  if (!reviewSettings.endpoint['put-reply']) {
    throw new CallError('params', {}, 400, { message: 'Missing value for RNR edit-reply endpoint' });
  }
  if (!reviewSettings.apiKey) {
    throw new CallError('params', {}, 400, { message: 'Missing value for RNR api key' });
  }

  const missingParams = getMissingKeys(params, requiredParams);
  let badParams = [];
  if (Object.keys(params).length) {
    badParams = getBadParams(params, paramsRules);
  }
  if (missingParams.length || badParams.length) {
    throw new CallError('params', null, 404, { missingParams, badParams });
  }

  const { accessToken, replyId, imsUserId, title, text, authorName } = params;
  const requestUrl = reviewSettings.endpoint['put-reply'].replace('{id}', replyId);
  const response = await fetch(requestUrl, {
    signal,
    method: 'PUT',
    headers: {
      accept: 'application/vnd.adobe-review.review-reply-v1+json',
      authorization: accessToken,
      'Content-Type': 'application/vnd.adobe-review.review-reply-edit-v1+json',
      'x-api-key': reviewSettings.apiKey,
      'x-gw-ims-user-id': imsUserId,
    },
    body: JSON.stringify({
      title,
      text,
      authorName,
    }),
  });
  /**
   * @type {AddReplyResponse}
   */
  let content = await response.text();
  try {
    content = JSON.parse(content);
  } catch (e) {
    // response is not json
  }

  if (!response.ok) {
    throw new CallError('http', content, response.status, { message: response.statusText });
  } else {
    return content;
  }
};
